import * as React from 'react';
import { Check, Truck, User, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TASK_STATUS, type Task } from '@klareospace/pharos-lib-types';
import {
  Button,
  cn,
  DRAWER_FIELD_LABEL_CLASSES,
  ErrorBanner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
} from '@klareospace/pharos-lib-web-utils';

import { composeStatusErrorMessage } from '&/features/orders/utils';

import { useTaskCarrierDriverFields } from '../hooks/use-task-carrier-driver-fields';
import {
  NONE_CARRIER_VALUE,
  NONE_DRIVER_VALUE,
  resolveAssignmentBadgeKey,
  resolveDriverName,
  TASK_ASSIGNMENT_MODE,
  type AssignmentSelectOption,
} from '../utils/task-assignment';
import { TaskDetailSection } from './task-detail-section';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TaskCarrierDriverSectionProps {
  /** Called with the fresh Task after a successful assignment/self-assignment, so the drawer reflects it immediately. */
  readonly onTaskUpdated: (task: Task) => void;
  readonly task: Task;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Bordered chip look for the carrier acceptance badge, overriding `StatusBadge`'s default pill shape. */
const ASSIGNMENT_BADGE_STYLES: Record<'carrierAccepted' | 'carrierRejected', string> = {
  carrierAccepted:
    'h-5 rounded-md border px-1.5 py-0 text-[10px] font-medium bg-success/10 text-success border-success/30',
  carrierRejected:
    'h-5 rounded-md border px-1.5 py-0 text-[10px] font-medium bg-destructive/10 text-destructive border-destructive/30',
};

/** Card wrapper shared by the Carrier and Driver fields, per the mockup. */
const FIELD_CARD_CLASSES = 'min-w-0 rounded-lg border border-border bg-muted/30 p-3 space-y-1.5';

/** Field label row: small leading icon + the shared drawer label style (its own bottom margin is dropped — the card's `space-y-1.5` handles spacing). */
const FIELD_LABEL_CLASSES = cn(DRAWER_FIELD_LABEL_CLASSES, 'flex items-center gap-1 mb-0');

// ─── Sub-components ───────────────────────────────────────────────────────────

interface CarrierFieldProps {
  readonly carrierName: string | undefined;
  readonly disabled: boolean;
  readonly editable: boolean;
  readonly onChange: (carrierId: string) => void;
  readonly options: AssignmentSelectOption[];
  readonly showNoneOption: boolean;
  readonly value: string;
}

function CarrierField({
  carrierName,
  disabled,
  editable,
  onChange,
  options,
  showNoneOption,
  value,
}: CarrierFieldProps): React.JSX.Element {
  const { t } = useTranslation('tasks');

  if (!editable) {
    return <span className="text-sm font-medium text-foreground">{carrierName ?? t('assignment.noCarrier')}</span>;
  }

  return (
    <Select disabled={disabled} onValueChange={onChange} value={value}>
      <SelectTrigger>
        <SelectValue placeholder={t('assignment.carrierPlaceholder')} />
      </SelectTrigger>
      <SelectContent>
        {showNoneOption && <SelectItem value={NONE_CARRIER_VALUE}>{t('assignment.noCarrier')}</SelectItem>}
        {options.map((opt) => (
          <SelectItem disabled={opt.disabled} key={opt.id} value={opt.id}>
            {opt.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface RejectionBannerProps {
  readonly statusReason: string | undefined;
}

/** Renders the rejection-reason banner. Only rendered by the caller when the task was actually rejected. */
function RejectionBanner({ statusReason }: RejectionBannerProps): React.JSX.Element {
  const { t } = useTranslation('tasks');
  const tOrders = (key: string): string => t(`orders:${key}`);

  return <ErrorBanner message={composeStatusErrorMessage(tOrders, t('errors.carrier_rejected'), statusReason)} />;
}

interface SaveCancelActionsProps {
  readonly canSave: boolean;
  readonly isDirty: boolean;
  readonly isPending: boolean;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}

/** Renders the Save/Cancel actions only once the draft is dirty — hidden again on Cancel or in read-only mode. */
function SaveCancelActions({
  canSave,
  isDirty,
  isPending,
  onCancel,
  onSave,
}: SaveCancelActionsProps): React.JSX.Element | null {
  const { t } = useTranslation('tasks');

  if (!isDirty) return null;

  return (
    <div className="flex gap-2 mt-4">
      <Button disabled={!canSave || isPending} onClick={onSave} type="button">
        {t('assignment.save')}
      </Button>
      <Button disabled={isPending} onClick={onCancel} type="button" variant="outline">
        {t('assignment.cancel')}
      </Button>
    </div>
  );
}

interface DriverFieldProps {
  readonly disabled: boolean;
  readonly driverName: string | undefined;
  readonly editable: boolean;
  readonly onChange: (driverId: string) => void;
  readonly options: AssignmentSelectOption[];
  readonly showNoneOption: boolean;
  readonly value: string;
}

function DriverField({
  disabled,
  driverName,
  editable,
  onChange,
  options,
  showNoneOption,
  value,
}: DriverFieldProps): React.JSX.Element {
  const { t } = useTranslation('tasks');

  if (!editable) {
    return <span className="text-sm font-medium text-foreground">{driverName ?? t('noDriver')}</span>;
  }

  return (
    <Select disabled={disabled} onValueChange={onChange} value={value}>
      <SelectTrigger>
        <SelectValue placeholder={t('assignment.driverPlaceholder')} />
      </SelectTrigger>
      <SelectContent>
        {showNoneOption && <SelectItem value={NONE_DRIVER_VALUE}>{t('noDriver')}</SelectItem>}
        {options.map((opt) => (
          <SelectItem key={opt.id} value={opt.id}>
            {opt.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Carrier & Driver assignment form. Permission- and status-driven (see `useTaskCarrierDriverFields`
 * for mode resolution and draft lifecycle) — this component only renders.
 */
export function TaskCarrierDriverSection({ onTaskUpdated, task }: TaskCarrierDriverSectionProps): React.JSX.Element {
  const { t } = useTranslation('tasks');

  const {
    canSave,
    carrierOptions,
    draftCarrierId,
    draftDriverId,
    driverFieldDisabled,
    driverOptions,
    handleCancel,
    handleCarrierChange,
    handleDriverChange,
    handleSave,
    isCarrierEditable,
    isDirty,
    isDriverEditable,
    isPending,
    mode,
    showDriverNoneOption,
  } = useTaskCarrierDriverFields(task, onTaskUpdated);

  const banner =
    task.status === TASK_STATUS.CARRIER_REJECTED ? (
      <RejectionBanner statusReason={task.statusReason ?? undefined} />
    ) : undefined;
  const assignmentBadgeKey = resolveAssignmentBadgeKey(task.status);

  return (
    <TaskDetailSection banner={banner} icon={Truck} title={t('assignment.carrierAndDriver')}>
      <div className="grid grid-cols-2 gap-4">
        <div className={FIELD_CARD_CLASSES}>
          <div className="flex items-center justify-between gap-2">
            <p className={FIELD_LABEL_CLASSES}>
              <Truck className="w-3 h-3" />
              {t('assignment.carrier')}
            </p>
            {assignmentBadgeKey !== undefined && (
              <StatusBadge
                icon={assignmentBadgeKey === 'carrierAccepted' ? Check : X}
                label={t(`assignment.${assignmentBadgeKey}`)}
                status={assignmentBadgeKey}
                styles={ASSIGNMENT_BADGE_STYLES}
                title={t(`assignment.${assignmentBadgeKey}Title`)}
              />
            )}
          </div>
          <CarrierField
            carrierName={task.carrier?.name}
            disabled={isPending}
            editable={isCarrierEditable}
            onChange={handleCarrierChange}
            options={carrierOptions}
            showNoneOption={mode === TASK_ASSIGNMENT_MODE.ASSIGN}
            value={draftCarrierId}
          />
        </div>
        <div className={FIELD_CARD_CLASSES}>
          <div className="flex items-center justify-between gap-2">
            <p className={FIELD_LABEL_CLASSES}>
              <User className="w-3 h-3" />
              {t('assignment.driver')}
            </p>
          </div>
          <DriverField
            disabled={driverFieldDisabled}
            driverName={resolveDriverName(task.driver)}
            editable={isDriverEditable}
            onChange={handleDriverChange}
            options={driverOptions}
            showNoneOption={showDriverNoneOption}
            value={draftDriverId}
          />
        </div>
      </div>
      <SaveCancelActions
        canSave={canSave}
        isDirty={isDirty}
        isPending={isPending}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </TaskDetailSection>
  );
}
